import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { jobsApi, customersApi, techniciansApi, estimatesApi, invoicesApi } from "../lib/api";
import { jobStatusColors } from "../data/jobsMock";
import { getJobColor } from "../data/dispatchMock";
import { JobDetail } from "./Jobs";
import { DocDetail } from "./Estimates";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";

/**
 * Job History (desktop) — completed/canceled jobs with a From/To date
 * filter, for admin/dispatcher/office. `ResponsivePage` (App.js) swaps this
 * out for the dedicated `MobileJobHistory` component below the md breakpoint,
 * so this component only ever renders at >=768px. Rows open the same
 * JobDetail side-drawer used on the Jobs page.
 */

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const getWeekBounds = (d) => {
  const day = d.getDay();
  const monday = new Date(d);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() + (day === 0 ? -6 : 1 - day));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { startStr: ymd(monday), endStr: ymd(sunday) };
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function JobHistory() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(() => getWeekBounds(new Date()).startStr);
  const [to, setTo] = useState(() => getWeekBounds(new Date()).endStr);
  const [selectedId, setSelectedId] = useState(null);
  const [overlayEstimate, setOverlayEstimate] = useState(null);
  const [profileCustomerId, setProfileCustomerId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [j, c, t, e, inv] = await Promise.all([
        jobsApi.list(),
        customersApi.list(),
        techniciansApi.list(),
        estimatesApi.list(),
        invoicesApi.list(),
      ]);
      setJobs(j || []);
      setCustomers(c || []);
      setTechs(t || []);
      setEstimates(e || []);
      setInvoices(inv || []);
    } catch {
      setJobs([]);
      toast.error("Could not load job history");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const historyJobs = useMemo(() => {
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return jobs
      .filter((j) => ["Completed", "Canceled"].includes(j.status))
      .filter((j) => {
        if (!fromMs && !toMs) return true;
        const t = j.start ? new Date(j.start).getTime() : 0;
        if (!t) return false;
        if (fromMs && t < fromMs) return false;
        if (toMs && t > toMs) return false;
        return true;
      })
      .sort((a, b) => (b.start || "").localeCompare(a.start || ""));
  }, [jobs, from, to]);

  const updateJob = (updated) => {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
  };

  const selected = jobs.find((j) => j.id === selectedId);

  return (
    <div className="p-6 space-y-4" data-testid="job-history-page">
      <div>
        <div className="text-[20px] font-bold mb-1 text-slate-900">Job History</div>
        <div className="text-[13px] mb-4 text-slate-500">Completed and canceled jobs, filtered by date.</div>

        <div className="rounded-xl p-3 flex items-center gap-2 mb-3 max-w-md bg-white border border-slate-200" data-testid="job-history-date-filter">
          <div className="flex-1">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">From date</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={to || undefined}
              data-testid="job-history-from"
              className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          <div className="flex-1">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">To date</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              min={from || undefined}
              data-testid="job-history-to"
              className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          {(from || to) && (
            <button
              onClick={() => { setFrom(""); setTo(""); }}
              data-testid="job-history-clear"
              className="self-end h-9 px-3 rounded-md text-[11.5px] font-semibold text-blue-600 hover:bg-blue-50"
            >
              Clear
            </button>
          )}
        </div>

        {loading ? (
          <div className="mt-6 flex items-center justify-center text-[14px] text-slate-500">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading history…
          </div>
        ) : historyJobs.length === 0 ? (
          <div className="px-6 pt-16 text-center" data-testid="job-history-empty">
            <Clock className="h-10 w-10 mx-auto mb-3 text-slate-400" />
            <div className="text-[15px] font-semibold text-slate-900">No history yet</div>
            <div className="text-[13px] mt-1 text-slate-500">
              {from || to ? "No completed jobs in this date range." : "Completed and canceled jobs show here."}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="job-history-table">
            <div className="grid grid-cols-[130px_minmax(180px,1fr)_minmax(200px,1.4fr)_140px_140px_140px_120px_36px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
              <div>Date</div>
              <div>Customer</div>
              <div>Address</div>
              <div>Type</div>
              <div>Status</div>
              <div>Technician</div>
              <div>Value</div>
              <div />
            </div>
            {historyJobs.map((j) => (
              <HistoryJobRow key={j.id} job={j} onClick={() => setSelectedId(j.id)} />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <JobDetail
          job={selected}
          customers={customers}
          techs={techs}
          estimates={estimates}
          invoices={invoices}
          onClose={() => setSelectedId(null)}
          onUpdate={updateJob}
          onCreateEstimate={async () => {
            try {
              let customerId = selected.customerId;
              if (!customerId) {
                const found = customers.find((c) => c.name === selected.customer);
                if (found) customerId = found.id;
              }
              if (!customerId) {
                const newCust = await customersApi.create({
                  name: selected.customer,
                  type: "Residential",
                  phone: selected.phone || "",
                  address: selected.address || "",
                  city: selected.city || "",
                  state: selected.state || "",
                  zip: selected.zip || "",
                });
                customerId = newCust.id;
                setCustomers((prev) => [newCust, ...prev]);
                try {
                  await jobsApi.patch(selected.id, { customerId });
                } catch (_) { /* non-blocking */ }
                toast.info(`Customer "${newCust.name}" auto-created`);
              }
              const created = await estimatesApi.create({
                customerId,
                jobId: selected.id,
                title: `${selected.type} — ${selected.customer}`,
                lineItems: [],
              });
              setEstimates((prev) => [created, ...prev]);
              setOverlayEstimate(created);
              toast.success(`Estimate ${created.id} created`);
            } catch (err) {
              console.error(err);
              toast.error(err?.response?.data?.detail || "Failed to create estimate");
            }
          }}
          onOpenEstimate={(est) => setOverlayEstimate(est)}
          onOpenCustomer={(customerId) => setProfileCustomerId(customerId)}
        />
      )}
      {overlayEstimate && (
        <DocDetail
          kind="estimate"
          doc={overlayEstimate}
          customers={customers}
          jobs={jobs}
          onClose={() => setOverlayEstimate(null)}
          onUpdated={(updated) => {
            setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            setOverlayEstimate(updated);
          }}
          onDeleted={(id) => {
            setEstimates((prev) => prev.filter((e) => e.id !== id));
            setOverlayEstimate(null);
          }}
          onConverted={(newInvoice) => {
            estimatesApi.get(overlayEstimate.id).then((e) => {
              setEstimates((prev) => prev.map((x) => (x.id === e.id ? e : x)));
            });
            toast.success(`Invoice ${newInvoice.id} created — open Estimates & Invoices to record payments`);
            setOverlayEstimate(null);
          }}
        />
      )}
      <CustomerProfileOverlay customerId={profileCustomerId} onClose={() => setProfileCustomerId(null)} />
    </div>
  );
}

function HistoryJobRow({ job, onClick }) {
  const sc = jobStatusColors[job.status] || jobStatusColors.Completed;
  const tc = getJobColor(job.type);
  return (
    <button
      data-testid={`job-history-row-${job.id}`}
      onClick={onClick}
      className="w-full text-left grid grid-cols-[130px_minmax(180px,1fr)_minmax(200px,1.4fr)_140px_140px_140px_120px_36px] gap-3 px-4 py-3 border-b border-slate-100 hover:bg-blue-50/40 transition-colors items-center"
    >
      <div className="text-[12px] font-semibold text-slate-700">{fmtDate(job.start)}</div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-slate-900 truncate">{job.customer || "—"}</div>
        <div className="text-[11px] text-slate-500 truncate">{job.id}</div>
      </div>
      <div className="min-w-0">
        <div className="text-[12.5px] text-slate-700 truncate">{job.address || "—"}</div>
        <div className="text-[11px] text-slate-500 truncate">{job.city}{job.city && job.state ? "," : ""} {job.state} {job.zip}</div>
      </div>
      <div>
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded" style={{ background: tc.bg, color: tc.text }}>
          {job.type || job.jobType || "Service"}
        </span>
      </div>
      <div>
        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: sc.dot }} />
          {job.status}
        </span>
      </div>
      <div className="text-[12.5px] text-slate-700 truncate">{job.tech || "Unassigned"}</div>
      <div className="text-[12.5px] font-semibold text-slate-700">{job.value ? `$${job.value.toLocaleString()}` : "—"}</div>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </button>
  );
}
